import * as Sentry from '@sentry/nextjs';
import 'client-only';

import type { DocumentUploadTelemetryContext } from './types';

/**
 * Records a Sentry breadcrumb for an in-flight client upload.
 *
 * NOTE: Browser uploads can run in parallel (e.g. the home split-upload queue
 * fans out up to three `mutateAsync` calls concurrently). Mutating the shared
 * Sentry scope here would let one upload's tags/context overwrite another's
 * and mis-attribute failures. Per-upload tagging is done at error-capture time
 * via `captureDocumentUploadErrorClient`, which uses `Sentry.withScope` to
 * fork an isolated scope. Breadcrumbs are timeline-additive so they're safe to
 * emit on the shared scope.
 *
 * Never throws.
 */
export function observeDocumentUploadClient(
  context: DocumentUploadTelemetryContext
): void {
  try {
    Sentry.addBreadcrumb({
      category: 'document_upload',
      message: 'flow_document_upload_started',
      level: 'info',
      data: {
        source: context.source,
        deal_id: context.dealId,
        package_id: context.packageId,
        flow_id: context.flowId,
        file_count: context.fileCount,
        total_bytes: context.totalBytes,
      },
    });
  } catch {
    // Observability must never interrupt uploads.
  }
}
